/**
 * IntakeUploadDialog — drag-drop admin tool for uploading intake xlsx
 * sheets. Powers the "Upload Intake" button on the Recruitment Funnel
 * widget and calls POST /api/admin/intake-import/upload.
 */
import { useState, useRef } from 'react';
import api from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { UploadCloud, FileSpreadsheet, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';

const INTAKE_OPTIONS = [
  'September 2026',
  'January 2027',
  'May 2027',
  'September 2027',
  'January 2028',
];

export default function IntakeUploadDialog({ open, onOpenChange, defaultIntake = 'September 2026', onImported }) {
  const [intake, setIntake] = useState(defaultIntake);
  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [appendMode, setAppendMode] = useState(true); // Default: safe append
  const inputRef = useRef(null);

  const reset = () => { setFile(null); setResult(null); setError(null); };

  const handleFile = (f) => {
    setError(null);
    setResult(null);
    if (!f) return;
    if (!/\.(xlsx|xls)$/i.test(f.name)) {
      setError('Only .xlsx / .xls files are accepted');
      return;
    }
    if (f.size > 20 * 1024 * 1024) {
      setError('File too large (max 20 MB)');
      return;
    }
    setFile(f);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    handleFile(e.dataTransfer.files?.[0]);
  };

  const handleUpload = async () => {
    if (!file || !intake) return;
    setUploading(true);
    setError(null);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append('intake_label', intake);
      fd.append('file', file);
      fd.append('append_mode', appendMode ? 'true' : 'false');
      const res = await api.post('/admin/intake-import/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
      setResult(res.data);
      toast.success(`Imported ${res.data.counts?.applications || 0} applications for ${intake}`);
      if (onImported) onImported(res.data);
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'Upload failed';
      setError(msg);
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent className="max-w-2xl bg-white dark:bg-slate-900" data-testid="intake-upload-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-gray-900 dark:text-white">
            <UploadCloud className="w-5 h-5 text-teal-600" />
            Upload Intake Sheet
          </DialogTitle>
          <DialogDescription className="text-xs text-gray-500 dark:text-gray-400">
            Drop your H E Direct intake xlsx (Applications + optional Conversions sheets) below. The importer detects sheets by name (must contain &quot;Application&quot; / &quot;Conversion&quot;) and maps each row to the CRM automatically. Re-uploading the same intake wipes prior records first (idempotent).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          <div>
            <Label className="text-xs">Target Intake</Label>
            <select
              value={intake}
              onChange={(e) => setIntake(e.target.value)}
              disabled={uploading}
              className="mt-1 w-full text-sm bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 rounded px-3 py-2 text-gray-800 dark:text-white"
              data-testid="intake-upload-target"
            >
              {INTAKE_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
            </select>
          </div>

          <div className={`rounded-lg border p-3 ${appendMode ? 'bg-emerald-50 border-emerald-200 dark:bg-emerald-900/20 dark:border-emerald-800' : 'bg-rose-50 border-rose-200 dark:bg-rose-900/20 dark:border-rose-800'}`}>
            <label className="flex items-start gap-3 cursor-pointer text-sm">
              <input
                type="checkbox"
                checked={appendMode}
                onChange={(e) => setAppendMode(e.target.checked)}
                disabled={uploading}
                className="mt-0.5"
                data-testid="intake-upload-append-mode"
              />
              <div>
                <div className="font-medium text-gray-900 dark:text-white">
                  {appendMode ? 'Add / merge into existing data (safe)' : 'Replace — wipe existing intake first (destructive)'}
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                  {appendMode
                    ? 'New rows are appended. Existing students are matched by name — duplicate applications for the same (student, uni, course) are skipped. Contact info (email/phone) on existing students is upgraded if this upload carries fresher values.'
                    : 'ALL records tagged for this intake will be deleted first, then the file is re-imported from scratch. Use this only when you want a full reset.'}
                </div>
              </div>
            </label>
          </div>

          <div
            onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
            onDragLeave={() => setDragActive(false)}
            onDrop={onDrop}
            onClick={() => inputRef.current?.click()}
            className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
              dragActive
                ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/20'
                : 'border-gray-300 dark:border-slate-600 hover:border-teal-400 hover:bg-gray-50 dark:hover:bg-slate-800/50'
            }`}
            data-testid="intake-upload-dropzone"
          >
            <input
              ref={inputRef}
              type="file"
              accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={(e) => handleFile(e.target.files?.[0])}
              className="hidden"
              disabled={uploading}
              data-testid="intake-upload-input"
            />
            {file ? (
              <div className="flex flex-col items-center gap-2">
                <FileSpreadsheet className="w-10 h-10 text-teal-600" />
                <div className="text-sm font-medium text-gray-900 dark:text-white">{file.name}</div>
                <div className="text-xs text-gray-500">{(file.size / 1024).toFixed(1)} KB — click to replace</div>
              </div>
            ) : (
              <>
                <UploadCloud className="w-10 h-10 mx-auto text-gray-400 mb-2" />
                <div className="text-sm text-gray-700 dark:text-gray-300 font-medium">Drop xlsx here, or click to browse</div>
                <div className="text-xs text-gray-500 mt-1">Expected sheets: &quot;APPLICATIONS&quot; and (optional) &quot;CONVERSIONS&quot;</div>
              </>
            )}
          </div>

          {error && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800 text-xs text-rose-700 dark:text-rose-300">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" /> {error}
            </div>
          )}

          {result && (
            <div className="p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 text-xs text-emerald-800 dark:text-emerald-200" data-testid="intake-upload-result">
              <div className="flex items-center gap-2 font-semibold mb-2">
                <CheckCircle2 className="w-4 h-4" /> Import complete — {result.intake_label} · {result.append_mode ? 'Appended' : 'Replaced'}
              </div>
              <div className="grid grid-cols-2 gap-1">
                <span>Applications added: <b>{result.counts?.applications ?? 0}</b></span>
                <span>Students added: <b>{result.counts?.students ?? 0}</b></span>
                <span>Leads added: <b>{result.counts?.leads ?? 0}</b></span>
                <span>Agents added: <b>{result.counts?.agents ?? 0}</b></span>
                {result.append_mode && (
                  <>
                    <span>Dup apps skipped: <b>{result.apps_skipped_duplicate ?? 0}</b></span>
                    <span>Contact upgrades: <b>{result.contact_upgrades ?? 0}</b></span>
                  </>
                )}
              </div>
              {result.conversions_merged || result.conversions_added ? (
                <div className="mt-2 pt-2 border-t border-emerald-200 dark:border-emerald-800">
                  Conversions merged: <b>{result.conversions_merged || 0}</b> · added: <b>{result.conversions_added || 0}</b>
                </div>
              ) : null}
              {result.journey_stage_breakdown ? (
                <div className="mt-2 pt-2 border-t border-emerald-200 dark:border-emerald-800">
                  <div className="font-semibold mb-1">Journey stages:</div>
                  {Object.entries(result.journey_stage_breakdown).map(([k, v]) => (
                    <div key={k} className="flex justify-between"><span>{k}</span><b>{v}</b></div>
                  ))}
                </div>
              ) : null}
              {result.cleared_previous ? (
                <div className="mt-2 pt-2 border-t border-emerald-200 dark:border-emerald-800 text-[10px] opacity-80">
                  Cleared prior rows: {Object.entries(result.cleared_previous).map(([k, v]) => `${k}=${v}`).join(', ')}
                </div>
              ) : null}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={uploading}>
              {result ? 'Close' : 'Cancel'}
            </Button>
            <Button
              onClick={handleUpload}
              disabled={!file || uploading}
              className="bg-teal-600 hover:bg-teal-700 text-white"
              data-testid="intake-upload-submit"
            >
              {uploading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Importing...</>
              ) : (
                <><UploadCloud className="w-4 h-4 mr-2" /> Upload &amp; Import</>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
